// Warning hazard tags — pure, RN-portable. Reads the NWS impact-based-warning (IBW)
// parameters off a warning's CAP `parameters` bag into a tidy, display-ready shape.
//
// Observed tag values (from live SVR/TOR warnings):
//   maxWindGust: "70 MPH"      maxHailSize: "Up to .75" | "1.00"
//   tornadoDetection: "POSSIBLE" | "OBSERVED"      waterspoutDetection: "POSSIBLE"
//   windThreat / hailThreat: "OBSERVED" | "RADAR INDICATED"
//   thunderstormDamageThreat: "CONSIDERABLE" | "DESTRUCTIVE"
//   tornadoDamageThreat: "CONSIDERABLE" | "CATASTROPHIC"

import type { NwsParameters } from '@/lib/nws/types'

// NWS tornado/waterspout detection: "RADAR INDICATED" is the DEFAULT for a routine
// Tornado Warning; "OBSERVED" means confirmed on the ground; "POSSIBLE" tags a SVR.
export type DetectionLevel = 'POSSIBLE' | 'RADAR INDICATED' | 'OBSERVED'
export type ThreatSource = 'OBSERVED' | 'RADAR INDICATED'
export type DamageThreat = 'CONSIDERABLE' | 'DESTRUCTIVE' | 'CATASTROPHIC'

export interface WarningHazards {
  windGustMph: number | null
  /** Hail diameter in inches (a "1.00", or the upper bound of "Up to .75"). */
  hailInches: number | null
  windThreat: ThreatSource | null
  hailThreat: ThreatSource | null
  tornado: DetectionLevel | null
  waterspout: DetectionLevel | null
  /** PDS / "considerable"+ damage tag — the loudest single hazard signal. */
  damageThreat: DamageThreat | null
}

const first = (p: NwsParameters | undefined, k: string): string | undefined => p?.[k]?.[0]

function numFrom(v: string | undefined): number | null {
  if (!v) return null
  const m = /(\d+(?:\.\d+)?|\.\d+)/.exec(v)
  return m ? Number(m[1]) : null
}

function upper<T extends string>(v: string | undefined, allowed: readonly T[]): T | null {
  if (!v) return null
  const u = v.trim().toUpperCase()
  return (allowed as readonly string[]).includes(u) ? (u as T) : null
}

export function parseHazards(parameters: NwsParameters | undefined): WarningHazards {
  return {
    windGustMph: numFrom(first(parameters, 'maxWindGust')),
    hailInches: numFrom(first(parameters, 'maxHailSize')),
    windThreat: upper(first(parameters, 'windThreat'), ['OBSERVED', 'RADAR INDICATED'] as const),
    hailThreat: upper(first(parameters, 'hailThreat'), ['OBSERVED', 'RADAR INDICATED'] as const),
    tornado: upper(first(parameters, 'tornadoDetection'), ['POSSIBLE', 'RADAR INDICATED', 'OBSERVED'] as const),
    waterspout: upper(first(parameters, 'waterspoutDetection'), ['POSSIBLE', 'RADAR INDICATED', 'OBSERVED'] as const),
    damageThreat:
      upper(first(parameters, 'tornadoDamageThreat'), ['CONSIDERABLE', 'CATASTROPHIC'] as const) ??
      upper(first(parameters, 'thunderstormDamageThreat'), ['CONSIDERABLE', 'DESTRUCTIVE'] as const),
  }
}

/** "1.00" → "1\"", ".75" → "¾\"" (nearest common hail-size fraction), for compact display. */
export function hailLabel(inches: number | null): string | null {
  if (inches == null) return null
  const frac: [number, string][] = [
    [0.25, '¼"'],
    [0.5, '½"'],
    [0.75, '¾"'],
    [1, '1"'],
    [1.25, '1¼"'],
    [1.5, '1½"'],
    [1.75, '1¾"'],
    [2, '2"'],
    [2.5, '2½"'],
    [2.75, '2¾"'],
    [3, '3"'],
    [4, '4"'],
  ]
  // Never understate giant/extreme hail — render the raw size above the table max.
  const max = frac[frac.length - 1][0]
  if (inches > max) return `${inches}"`
  // `<=` so an exact tie rounds UP to the larger size (safer for a hazard readout).
  let best = frac[0]
  for (const f of frac) if (Math.abs(f[0] - inches) <= Math.abs(best[0] - inches)) best = f
  return best[1]
}
